import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-year.js

/**
 * CarbonYearHandler
 * 
 * Manages year-level carbon accounting operations:
 * - Complete cascade deletion of all data for specific years
 * - Sequential cleanup of emission results, activities, distributions, suppliers, and spend volumes
 * - Error handling with detailed collection of any issues
 * - Progress tracking with callback support for UI feedback
 * - Database synchronization to ensure complete data integrity
 */
export class CarbonYearHandler {
  /**
   * Initialize the carbon year handler with a store reference
   * @param {Object} store - The main application store instance
   */
  constructor(store) {
    this.store = store;
  }

  /**
   * Delete all data associated with a specific year
   * Performs a complete cascade deletion of all carbon accounting data
   * for the specified year including activities, distributions, suppliers,
   * spend volumes, and emission results.
   * 
   * @param {string|number} year - Year to delete data for
   * @param {Function} [progressCallback] - Optional callback for progress updates (receives step, total, message)
   * @returns {Promise<boolean>} Success status
   * @throws {Error} If deletion fails with a user-friendly error message
   */
  async deleteYearData(year, content, progressCallback = null) {
    try {
      const errors = [];
      const projectId = this.store.getProjectId();

      if (!projectId) {
        throw new Error('Project ID is required to delete year data');
      }

      // Define deletion steps for progress tracking
      const steps = [
        { name: 'preemptive', message: content?.deletionPreemptive || 'Preparing for data deletion...' },
        { name: 'emission-results', message: content?.deletionEmissions || 'Deleting emission results...' },
        { name: 'activities', message: content?.deletionActivities || 'Deleting activity data...' },
        { name: 'distributions', message: content?.deletionDistributions || 'Deleting spend distributions...' },
        { name: 'suppliers', message:  content?.deletionSuppliers || 'Deleting supplier inputs...' },
        { name: 'spend', message:  content?.deletionSpend || 'Deleting spend volumes...' },
        { name: 'sync', message:  content?.deletionSync || 'Synchronizing with database...' },
        { name: 'tiern-recalc', message: content?.deletionRecalc || 'Updating performance...' },
        { name: 'refresh', message:  content?.deletionRefresh || 'Refreshing data...' }
      ];

      // Track current step
      let currentStep = 0;
      const totalSteps = steps.length;

      // Helper to update progress
      const updateProgress = () => {
        if (progressCallback) {
          progressCallback(
            currentStep + 1,
            totalSteps,
            steps[currentStep].message
          );
        }
        currentStep++;
      };

      // Step 1: Pre-emptively flag year for recalculation using local-only approach
      updateProgress();
      if (this.store.state.data.emissionResults?.byYear?.[year]) {
        this.store.state.data.emissionResults.byYear[year] =
          this.store.state.data.emissionResults.byYear[year].map(item => ({
            ...item,
            needsRecalculation: true
          }));
        this.store.notifySubscribers('emissionResults');

        if (process.env.NODE_ENV === 'development') {
          console.log(`Pre-emptively flagged year ${year} for recalculation before deletion`);
        }
      }

      // Step 2: Delete emission results first 
      updateProgress();
      await this.deleteEmissionResults(year, errors, projectId);

      // Step 3: Delete activities
      updateProgress();
      await this.deleteActivities(year, errors, projectId);

      // Step 4: Delete spend distributions
      updateProgress();
      await this.deleteSpendDistributions(year, errors, projectId);

      // Step 5: Delete supplier inputs
      updateProgress();
      await this.deleteSupplierInputs(year, errors, projectId);

      // Step 6: Delete spend volume
      updateProgress();
      await this.deleteSpendVolume(year, errors, projectId);

      // If any critical errors occurred, log them but continue
      if (errors.length > 0) {
        // Filter out entry not found errors as they're not critical
        const criticalErrors = errors.filter(e =>
          !String(e.error || e.message || "").includes("not found")
        );

        if (criticalErrors.length > 0 && process.env.NODE_ENV === 'development') {
          console.warn(`Encountered ${criticalErrors.length} errors during year deletion`);
        }
      }

      // Step 7: Force explicit sync with Snowflake to ensure complete cleanup
      updateProgress();
      await Promise.allSettled([
        // Force cleanup sync operations WITH YEAR PARAMETER - using allSettled to continue even if some fail
        fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/ca-activities-data/project/${projectId}/cleanup`,
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getStrapiToken()}` 
            },
            body: JSON.stringify({ year })
          }
        ),
        fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/cleanup`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getStrapiToken()}`
            },
            body: JSON.stringify({ year }) // Pass year to ensure year-specific cleanup
          }
        ),
        fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-distributions/project/${projectId}/cleanup`,
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getStrapiToken()}` 
            },
            body: JSON.stringify({ year })
          }
        ),
        fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/ca-supplier-inputs/project/${projectId}/cleanup`,
          {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getStrapiToken()}` 
            },
            body: JSON.stringify({ year })
          }
        )
      ]);

      // Clean up orphaned supplier inputs
      await this.store.supplierInputHandler.cleanupOrphanedSupplierInputs();

      // Step 8: Recalculate Tier-N performance without SAVE_AND_CALC
      updateProgress();
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Recalculating Tier-N performance after year ${year} deletion`);
        }

        const recalculateResponse = await fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/ca-emission-results/project/${projectId}/recalculate-tiern`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getStrapiToken()}`
            }
          }
        );

        if (!recalculateResponse.ok) {
          const errorText = await recalculateResponse.text();
          console.warn(
            `Tier-N performance recalculation failed: ${recalculateResponse.status} ${recalculateResponse.statusText} - ${errorText}`
          );
          // Non-critical - continue with refresh
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Successfully recalculated Tier-N performance`);
          }

          // Refresh business segments with updated performance data from Snowflake
          if (this.store.businessSegmentsHandler) {
            try {
              if (process.env.NODE_ENV === 'development') {
                console.log('Refreshing business segments after Tier-N recalculation...');
              }
              await this.store.businessSegmentsHandler.loadBusinessSegments(true);
              if (process.env.NODE_ENV === 'development') {
                console.log('Business segments refreshed successfully');
              }
            } catch (refreshError) {
              console.warn('Failed to refresh business segments:', refreshError);
              // Non-critical - continue with refresh
            }
          }
        }
      } catch (recalculateError) {
        console.warn(`Error recalculating Tier-N performance:`, recalculateError);
        // Non-critical - continue with refresh
      }

      // Step 9: Refresh all data in parallel
      updateProgress();
      await Promise.all([
        this.store.activitiesHandler.loadActivityData(),
        this.store.spendHandler.loadSpendVolume(),
        this.store.spendDistributionHandler.loadSpendDistribution(),
        this.store.supplierInputHandler.loadSupplierInput(),
        this.store.emissionsHandler.loadAllEmissionResults()
      ]);

      // Operation Lightsaber: Mark suppliers as stale after CA year deletion
      if (this.store.supplychainSuppliersHandler) {
        this.store.supplychainSuppliersHandler.markStale('CA year deleted');
      }

      // Save final state
      await this.store.saveState();

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`Failed to delete year ${year}:`, error);
      }

      // Rethrow with a user-friendly message
      const friendlyError = new Error(`Could not delete year ${year}: ${error.message}`);
      friendlyError.originalError = error;
      throw friendlyError;
    }
  }

  /**
   * Delete emission results for a specific year
   * @private
   * @param {string} year - Year to delete emission results for
   * @param {Array} errors - Array to collect errors
   * @param {string} projectId - Project ID for API calls
   * @returns {Promise<void>}
   */
  async deleteEmissionResults(year, errors, projectId) {
    if (!projectId) {
      errors.push({ type: 'emission_results', error: 'Project ID is required to delete emission results' });
      return;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Deleting emission results for project ${projectId}, year ${year}`);
      }

      // Use the deleteByYear endpoint
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-emission-results/project/${projectId}/year/${year}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${getStrapiToken()}`
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete emission results: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Successfully deleted emission results for year ${year}:`, result);
      }

    } catch (error) {
      console.error(`Error deleting emission results for year ${year}:`, error);
      errors.push({ type: 'emission_results', error: error.message });
    }
  }

  /**
   * Delete all activities for a specific year
   * @private
   * @param {string} year - Year to delete activities for
   * @param {Array} errors - Array to collect errors
   * @param {string} projectId - Project ID for API calls
   * @returns {Promise<void>}
   */
  async deleteActivities(year, errors, projectId) {
    if (!projectId) {
      errors.push({ type: 'activity', error: 'Project ID is required to delete activities' });
      return;
    }

    try {
      // Make sure we have the latest activity data
      await this.store.activitiesHandler.loadActivityData();

      const scope1Activities = this.store.state.data.activityData?.byYear[year]?.scope1 || [];
      const scope2Activities = this.store.state.data.activityData?.byYear[year]?.scope2 || [];

      if (process.env.NODE_ENV === 'development') {
        console.log(`Deleting ${scope1Activities.length} scope1 and ${scope2Activities.length} scope2 activities for year ${year}`);
      }

      // Delete individual activities with error handling for each
      const allActivities = [...scope1Activities, ...scope2Activities];
      for (const activity of allActivities) {
        try {
          if (activity && activity.id) {
            // Get the scope for this activity
            const scope = activity.scope || (scope1Activities.includes(activity) ? 'scope1' : 'scope2');

            // Delete this specific activity
            await this.store.activitiesHandler.deleteActivity(year, scope, activity.id);
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`Deleted activity ${activity.id} for year ${year}, scope ${scope}`);
            }
          }
        } catch (activityError) {
          // Don't fail the entire operation for one activity
          if (process.env.NODE_ENV === 'development') {
            console.warn(`Error deleting activity ${activity?.id}:`, activityError);
          }
          
          // Only collect errors that are not "Entry not found" 
          // because that just means it's already deleted
          if (!activityError.message?.includes('not found')) {
            errors.push({
              type: 'activity',
              id: activity?.id,
              error: activityError.message
            });
          }
        }
      }

      // Final cleanup sync WITH YEAR - this will clean up any activities we missed
      // This is handled in the main sync step now with year parameter

      if (process.env.NODE_ENV === 'development') {
        console.log(`Completed activity deletion for year ${year}`);
      }

    } catch (error) {
      console.error(`Error deleting activities for year ${year}:`, error);
      errors.push({ type: 'activity', error: error.message });
    }
  }

  /**
   * Delete all spend distributions for a specific year
   * @private
   * @param {string} year - Year to delete distributions for
   * @param {Array} errors - Array to collect errors
   * @param {string} projectId - Project ID for API calls
   * @returns {Promise<void>}
   */
  async deleteSpendDistributions(year, errors, projectId) {
    if (!projectId) {
      errors.push({ type: 'distribution', error: 'Project ID is required to delete spend distributions' });
      return;
    }

    const distributions = this.store.state.data.spendDistribution?.byYear[year] || [];

    // Delete individual distributions
    for (const dist of distributions) {
      try {
        await this.store.spendDistributionHandler.deleteSpendDistribution(dist.id);
      } catch (error) {
        errors.push({ type: 'distribution', id: dist.id, error: error.message });
      }
    }

    // Final cleanup sync WITH YEAR is handled in the main sync step

    // Flag for recalculation using shared utility
    await this.store.emissionsHandler.flagYearForRecalculation(year);
  }

  /**
   * Delete all supplier inputs for a specific year
   * @private
   * @param {string} year - Year to delete supplier inputs for
   * @param {Array} errors - Array to collect errors
   * @param {string} projectId - Project ID for API calls
   * @returns {Promise<void>}
   */
  async deleteSupplierInputs(year, errors, projectId) {
    if (!projectId) {
      errors.push({ type: 'supplierInput', error: 'Project ID is required to delete supplier inputs' });
      return;
    }

    const supplierInputs = this.store.state.data.supplierInput?.byYear[year] || [];

    if (process.env.NODE_ENV === 'development') {
      console.log(`Attempting to delete ${supplierInputs.length} supplier inputs for year ${year}`);
    }

    // Delete individual supplier inputs - with improved error handling for 404s
    for (const input of supplierInputs) {
      try {
        if (!input || !input.id) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Skipping supplier input without ID');
          }
          continue;
        }

        await this.store.supplierInputHandler.deleteSupplierInput(input);
      } catch (error) {
        // Only treat as an error if it's not a "not found" error
        if (error.message && error.message.includes('not found')) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Supplier input ${input.id} already deleted - skipping`);
          }
        } else {
          console.error(`Error deleting supplier input ${input.id}:`, error);
          errors.push({ type: 'supplierInput', id: input.id, error: error.message });
        }
      }
    }

    // Final cleanup sync WITH YEAR is handled in the main sync step

    // Flag for recalculation using shared utility
    await this.store.emissionsHandler.flagYearForRecalculation(year);
  }

  /**
   * Delete spend volume for a specific year
   * @private
   * @param {string} year - Year to delete spend volume for
   * @param {Array} errors - Array to collect errors
   * @param {string} projectId - Project ID for API calls
   * @returns {Promise<void>}
   */
  async deleteSpendVolume(year, errors, projectId) {
    if (!projectId) {
      errors.push({ type: 'spendVolume', error: 'Project ID is required to delete spend volume' });
      return;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Deleting all spend volume data for project ${projectId}, year ${year}`);
      }

      // First, try to use the dedicated year deletion endpoint to ensure complete deletion from Strapi
      try {
        const deleteResponse = await fetch(
          `${process.env.GATSBY_STRAPI_URL}/api/ca-spend-volumes/project/${projectId}/year/${year}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${getStrapiToken()}`
            }
          }
        );

        if (deleteResponse.ok) {
          const result = await deleteResponse.json();
          if (process.env.NODE_ENV === 'development') {
            console.log(`Deleted spend volume records using year endpoint:`, result);
          }
        } else {
          // If that fails, fall back to individual deletions
          throw new Error(`Year deletion endpoint failed: ${deleteResponse.status} ${deleteResponse.statusText}`);
        }
      } catch (deleteError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Year deletion endpoint failed, falling back to individual deletions:', deleteError);
        }

        // Fall back to deleting individual records
        const spendVolumes = this.store.state.data.spendVolume?.byYear[year] || [];
        for (const volume of spendVolumes) {
          try {
            if (volume && volume.id) {
              await this.store.spendHandler.deleteSpendRecord(volume.id);
            }
          } catch (volumeError) {
            if (!volumeError.message?.includes('not found')) {
              errors.push({ type: 'spendVolume', id: volume?.id, error: volumeError.message });
            }
          }
        }
      }

      // Ensure the state is updated
      await this.store.spendHandler.loadSpendVolume();

      // Final cleanup sync WITH YEAR is handled in the main sync step

      // Flag for recalculation using shared utility
      await this.store.emissionsHandler.flagYearForRecalculation(year);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Successfully deleted spend volume data for year ${year}`);
      }

    } catch (error) {
      console.error(`Error deleting spend volume for year ${year}:`, error);
      errors.push({ type: 'spendVolume', year, error: error.message });
    }
  }
}